import {
  ApplicationConfig,
  EnvironmentProviders,
  LOCALE_ID,
  Provider,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideErrorModule } from '@app/shared/errors/error.module';
import { provideIcons } from '@app/shared/icons/mat-icon.provider';
import { provideCdtMatPaginatorIntl } from '@app/shared/paginator';
import * as countries from 'i18n-iso-countries';
import frLocale from 'i18n-iso-countries/langs/fr.json';
import { vanexKeycloakProvider } from '@environments/keycloak/keycloak.provider';
import { RuntimeConfig } from '@environments/runtime-config';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { EMBEDDING } from './shared/shell/embedding.token';
import { Embedding } from './shared/shell/shell-protocol';
import { routes } from './app.routes';
import { AuthInterceptor } from './shared/auth/auth.interceptor';

countries.registerLocale(frLocale);

/**
 * Les providers dépendent de `/config.json` (adresse de l'API, annuaire) : ils ne peuvent être
 * construits qu'une fois ce fichier lu, d'où une fonction plutôt qu'une constante — voir `main.ts`.
 * `demo` s'ajoute à la fin, pour remplacer le réseau dans un onglet de démonstration.
 * `embedding` est la coque qui encadre l'application, `null` quand elle est ouverte seule.
 */
export const appConfig = (
  runtimeConfig: RuntimeConfig,
  demo: (Provider | EnvironmentProviders)[] = [],
  embedding: Embedding | null = null,
): ApplicationConfig => ({
  providers: [
    vanexKeycloakProvider(runtimeConfig.keycloak, embedding),
    { provide: EMBEDDING, useValue: embedding },
    ...appProviders(runtimeConfig, demo),
  ],
});

/**
 * Les providers de l'application, Keycloak excepté : le rejeu des parcours en CI les reprend
 * tels quels, avec un Keycloak simulé.
 */
export const appProviders = (
  runtimeConfig: RuntimeConfig,
  demo: (Provider | EnvironmentProviders)[] = [],
): (Provider | EnvironmentProviders)[] => [
  { provide: RUNTIME_CONFIG, useValue: runtimeConfig },
  provideBrowserGlobalErrorListeners(),
  provideRouter(routes),
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  provideHttpClient(withXhr(), withInterceptorsFromDi()),
  { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
  { provide: LOCALE_ID, useValue: 'fr-FR' },
  {
    provide: DateAdapter,
    useClass: MomentDateAdapter,
    deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
  },
  { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
  {
    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
    useValue: {
      subscriptSizing: 'dynamic',
    },
  },
  { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
  provideErrorModule(),
  provideCdtMatPaginatorIntl(),
  provideIcons(),
  ...demo,
];
